/**
 * 💜 SynastrySection - Sección de Compatibilidad de Pareja (Simplificada)
 * 
 * Permite al usuario calcular la compatibilidad numerológica con su pareja
 * usando el Número Personal (día de nacimiento) de cada uno.
 * 
 * @version 2.0 - 2026-02-02
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Users, Sparkles, AlertCircle, ExternalLink, Loader2, Save, Info, ChevronDown, Plus, Trash2, Check } from 'lucide-react';
import { 
  calculateSynastry, 
  getNumberMeaning,
  COUPLE_TYPES,
  NUMBER_GROUPS,
  SAME_GROUP_MEANINGS,
  getDifferentGroupMeaning,
  getPersonalNumberInRelationship,
  getSameNumberVariation,
  PINNACLE_POSITIONS
} from '../utils/synastryCalculations';

// Clave para localStorage (fallback)
const STORAGE_KEY = 'numerathum_partner_data';

/**
 * Obtiene el token de autenticación
 */
const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

/**
 * Carga datos de pareja desde localStorage (fallback)
 */
const loadPartnerDataLocal = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.warn('Error cargando datos de pareja local:', e);
  }
  return null;
};

/**
 * Guarda datos de pareja en localStorage (fallback)
 */
const savePartnerDataLocal = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Error guardando datos de pareja local:', e);
  }
};

/**
 * API: Cargar lista de parejas desde el servidor
 */
const fetchPartners = async () => {
  const token = getAuthToken();
  console.log('📋 [LIST] Token disponible:', token ? 'SÍ (' + token.substring(0,20) + '...)' : 'NO');
  
  if (!token) {
    console.warn('📋 [LIST] No hay token - usando fallback localStorage');
    return [];
  }
  
  try {
    const response = await fetch('/api/partner/list', {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    console.log('📋 [LIST] Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('📋 [LIST] Response error:', response.status, errorText);
      throw new Error('Error cargando parejas');
    }
    
    const data = await response.json();
    console.log('📋 [LIST] Response data:', data);
    
    return data.success ? data.data : [];
  } catch (error) {
    console.error('📋 [LIST] Error completo:', error);
    return [];
  }
};

/**
 * API: Guardar pareja en el servidor
 */
const savePartnerToServer = async (partnerData, partnerId = null) => {
  const token = getAuthToken();
  console.log('💾 [SAVE] Token disponible:', token ? 'SÍ (' + token.substring(0,20) + '...)' : 'NO');
  
  if (!token) {
    console.error('💾 [SAVE] No hay token - usando fallback localStorage');
    return null;
  }
  
  const payload = {
    id: partnerId,
    name: partnerData.name,
    apellidoPaterno: partnerData.lastName,
    apellidoMaterno: partnerData.scdLastName,
    birthDate: partnerData.birthDate
  };
  console.log('💾 [SAVE] Payload a enviar:', payload);
  
  try {
    const response = await fetch('/api/partner/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    
    console.log('💾 [SAVE] Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('💾 [SAVE] Response error:', response.status, errorText);
      throw new Error(`Error guardando pareja: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('💾 [SAVE] Response data:', data);
    
    if (!data.success) {
      console.error('💾 [SAVE] API retornó error:', data.error);
    }
    
    return data.success ? data.data : null;
  } catch (error) {
    console.error('💾 [SAVE] Error completo:', error);
    return null;
  }
};

/**
 * API: Activar pareja
 */
const activatePartnerOnServer = async (partnerId) => {
  const token = getAuthToken();
  if (!token) return false;
  
  try {
    const response = await fetch('/api/partner/activate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ partnerId })
    });
    
    return response.ok;
  } catch (error) {
    console.warn('Error activando pareja:', error);
    return false;
  }
};

/**
 * API: Eliminar pareja
 */
const deletePartnerFromServer = async (partnerId) => {
  const token = getAuthToken();
  if (!token) return false;
  
  try {
    const response = await fetch('/api/partner/delete', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ partnerId })
    });
    
    return response.ok;
  } catch (error) {
    console.warn('Error eliminando pareja:', error);
    return false;
  }
};

/**
 * Componente para mostrar un número personal grande
 */
function PersonalNumberDisplay({ number, name, group, isUser = false }) {
  const meaning = getNumberMeaning(number);
  
  return (
    <div className={`
      flex flex-col items-center p-4 rounded-2xl
      ${isUser ? 'bg-morado/5 border-morado/20' : 'bg-pink-50 border-pink-200'}
      border shadow-sm
    `}>
      <span className="text-xs text-gray-500 mb-1">{isUser ? 'Tu número' : 'Su número'}</span>
      
      {/* Número grande */}
      <div className={`
        w-20 h-20 rounded-full flex items-center justify-center
        text-3xl font-bold text-white shadow-lg
        ${isUser 
          ? 'bg-morado' 
          : 'bg-pink-600'
        }
      `}>
        {number}
      </div>
      
      {/* Nombre */}
      <span className={`mt-2 font-semibold ${isUser ? 'text-morado' : 'text-pink-700'}`}>
        {name}
      </span>
      
      {/* Keyword del número */}
      <span className="text-sm text-gray-600 mt-1">
        {meaning.keyword}
      </span>
      
      {/* Grupo */}
      {group && (
        <span className="text-xs text-gray-400 mt-1 flex items-center gap-1">
          {group.emoji} {group.name}
        </span>
      )}
    </div>
  );
}

/**
 * Componente para mostrar el resultado de compatibilidad
 */
function CompatibilityResult({ results }) {
  const { coupleType, coupleNumber, person1, person2, sameGroup, calculation } = results;
  
  // Colores según el tipo
  const typeColors = {
    natural: 'bg-emerald-600',
    compatible: 'bg-blue-600',
    challenge: 'bg-orange-500',
    neutral: 'bg-gray-500'
  };
  
  const bgColors = {
    natural: 'bg-green-50 border-green-200',
    compatible: 'bg-blue-50 border-blue-200',
    challenge: 'bg-orange-50 border-orange-200',
    neutral: 'bg-gray-50 border-gray-200'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header con el resultado principal */}
      <div className={`
        rounded-2xl p-6 text-center border shadow-lg
        ${bgColors[results.coupleTypeKey] || 'bg-gray-50 border-gray-200'}
      `}>
        {/* Tipo de pareja */}
        <div className="mb-4">
          <span className="text-4xl">{coupleType?.emoji}</span>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          {coupleType?.name || 'Compatibilidad'}
        </h3>
        <p className="text-gray-600">
          {coupleType?.shortDesc}
        </p>
      </div>

      {/* Números personales lado a lado */}
      <div className="grid grid-cols-2 gap-4">
        <PersonalNumberDisplay 
          number={person1.personalNumber}
          name={person1.name}
          group={person1.group}
          isUser={true}
        />
        <PersonalNumberDisplay 
          number={person2.personalNumber}
          name={person2.name}
          group={person2.group}
          isUser={false}
        />
      </div>

      {/* Número de Pareja (el resultado de la suma) */}
      <div className="bg-morado/10 rounded-2xl p-6 text-center border border-morado/20">
        <span className="text-xs text-gray-500 block mb-2">Número de Pareja</span>
        
        {/* Cálculo visual */}
        <div className="flex items-center justify-center gap-3 mb-3 text-lg">
          <span className="bg-morado/20 px-3 py-1 rounded-lg font-semibold text-morado">
            {calculation.day1}
          </span>
          <span className="text-gray-400">+</span>
          <span className="bg-pink-200 px-3 py-1 rounded-lg font-semibold text-pink-800">
            {calculation.day2}
          </span>
          <span className="text-gray-400">=</span>
          <span className="bg-gray-200 px-3 py-1 rounded-lg font-semibold text-gray-700">
            {calculation.sum}
          </span>
          <span className="text-gray-400">→</span>
          <div className={`
            w-14 h-14 rounded-full flex items-center justify-center
            text-xl font-bold text-white shadow-lg
            ${typeColors[results.coupleTypeKey] || 'bg-gray-500'}
          `}>
            {coupleNumber}
          </div>
        </div>
        
        <p className="text-sm text-gray-600">
          {getNumberMeaning(coupleNumber).keyword}
        </p>
      </div>

      {/* Descripción del tipo de pareja */}
      <div className={`rounded-2xl p-5 border ${bgColors[results.coupleTypeKey] || 'bg-gray-50 border-gray-200'}`}>
        <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          {coupleType?.emoji} {coupleType?.name}
        </h4>
        <p className="text-gray-700 text-sm leading-relaxed mb-4">
          {coupleType?.description}
        </p>
        <div className="bg-white/60 rounded-xl p-4 border border-white/80">
          <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
            <Info className="w-3 h-3" /> Consejo
          </h5>
          <p className="text-sm text-gray-600 leading-relaxed">
            {coupleType?.advice}
          </p>
        </div>
      </div>

      {/* Conexiones por Aspecto */}
      {results.connections && (
        <div className="bg-white/70 rounded-2xl p-5 border border-morado/20">
          <h4 className="font-semibold text-gray-800 mb-4">
            🔮 Conexiones por Aspecto
          </h4>
          
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(results.connections).map(([key, conn]) => (
              <div key={key} className="text-center p-3 bg-morado/5 rounded-xl">
                <span className="text-2xl">{conn.emoji}</span>
                <div className="w-12 h-12 mx-auto mt-2 rounded-full bg-white border-2 border-morado/20 flex items-center justify-center text-lg font-bold text-morado">
                  {conn.value}
                </div>
                <p className="text-xs text-gray-600 mt-2 font-medium">{conn.label}</p>
              </div>
            ))}
          </div>
          
          <p className="text-xs text-gray-500 text-center mt-3">
            Estos números revelan cómo se conectan en diferentes planos de la relación.
          </p>
        </div>
      )}

      {/* Año Personal de la Pareja */}
      {results.coupleYear && (
        <div className="bg-morado/5 rounded-2xl p-5 border border-morado/20">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-morado flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {results.coupleYear.value}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-indigo-800">
                📅 Año Personal de la Pareja {results.coupleYear.year}
              </h4>
              <p className="text-sm text-indigo-700 mt-1">
                <span className="font-semibold">{results.coupleYear.meaning.keyword}:</span>{' '}
                {results.coupleYear.meaning.inRelationship}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pináculo de Pareja (A, B, C, D) */}
      {results.pinnacle && (
        <div className="bg-white/70 rounded-2xl p-5 border border-morado/20 space-y-4">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            🏔️ Pináculo de Pareja
          </h4>
          <p className="text-xs text-gray-500 leading-relaxed">
            Cuatro aspectos fundamentales que definen la energía de su relación: lo que vienen a sanar, cómo funcionan juntos, qué los fortalece y cómo los percibe el mundo.
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            {['A', 'B', 'C', 'D'].map((pos) => {
              const pin = results.pinnacle[pos];
              const meta = PINNACLE_POSITIONS[pos];
              if (!pin || !pin.meaning) return null;
              
              const colorClasses = {
                A: { bg: 'bg-rose-50', border: 'border-rose-200', number: 'bg-rose-600', text: 'text-rose-800', tag: 'bg-rose-100 text-rose-700' },
                B: { bg: 'bg-morado/5', border: 'border-morado/20', number: 'bg-morado', text: 'text-morado', tag: 'bg-morado/10 text-morado' },
                C: { bg: 'bg-emerald-50', border: 'border-emerald-200', number: 'bg-emerald-600', text: 'text-emerald-800', tag: 'bg-emerald-100 text-emerald-700' },
                D: { bg: 'bg-indigo-50', border: 'border-indigo-200', number: 'bg-indigo-600', text: 'text-indigo-800', tag: 'bg-indigo-100 text-indigo-700' }
              };
              const c = colorClasses[pos];
              
              return (
                <div key={pos} className={`${c.bg} ${c.border} border rounded-xl p-4 space-y-3`}>
                  {/* Header: emoji + letra + número */}
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-full ${c.number} flex items-center justify-center text-white font-bold text-lg shadow-md`}>
                      {pin.value}
                    </div>
                    <div>
                      <span className={`font-semibold ${c.text} text-sm block`}>
                        {meta.emoji} {meta.label}
                      </span>
                      <span className="text-[10px] text-gray-400 leading-tight block">
                        {meta.description}
                      </span>
                    </div>
                  </div>
                  
                  {/* Descripción */}
                  <p className="text-xs text-gray-700 leading-relaxed">
                    {pin.meaning.text}
                  </p>
                  
                  {/* Keywords */}
                  <div className="flex flex-wrap gap-1">
                    {pin.meaning.keywords.map((kw, i) => (
                      <span key={i} className={`text-[10px] ${c.tag} px-1.5 py-0.5 rounded-full`}>
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          
        </div>
      )}

      {/* Información de grupos */}
      {person1.group && person2.group && (
        <div className="bg-white/70 rounded-2xl p-5 border border-morado/20 space-y-5">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            🎭 Grupos Numerológicos
          </h4>
          
          {/* Cards de cada grupo - si son del mismo grupo, mostrar UNA card combinada */}
          {sameGroup ? (
            // MISMO GRUPO: Una sola card que habla de ambos
            <div className="bg-morado/5 rounded-xl p-5 border border-morado/20">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-3xl">{person1.group.emoji}</span>
                <div className="text-center">
                  <span className="font-semibold text-morado block">
                    {person1.name} & {person2.name}
                  </span>
                  <span className="text-sm text-morado">Ambos pertenecen al {person1.group.name}</span>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed text-center">
                {person1.group.description}
              </p>
            </div>
          ) : (
            // GRUPOS DIFERENTES: Dos cards separadas
            <div className="grid md:grid-cols-2 gap-4">
              {/* Grupo persona 1 */}
              <div className="bg-morado/5 rounded-xl p-4 border border-morado/10">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{person1.group.emoji}</span>
                  <div>
                    <span className="font-semibold text-morado block">{person1.name}</span>
                    <span className="text-xs text-morado">{person1.group.name}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {person1.group.description}
                </p>
              </div>
              
              {/* Grupo persona 2 */}
              <div className="bg-pink-50 rounded-xl p-4 border border-pink-100">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{person2.group.emoji}</span>
                  <div>
                    <span className="font-semibold text-pink-800 block">{person2.name}</span>
                    <span className="text-xs text-pink-600">{person2.group.name}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {person2.group.description}
                </p>
              </div>
            </div>
          )}

          {/* Significado en la relación por NÚMERO PERSONAL (no por grupo) */}
          {(() => {
            const desc1 = getPersonalNumberInRelationship(person1.personalNumber);
            const desc2 = getPersonalNumberInRelationship(person2.personalNumber);
            const sameNumber = person1.personalNumber === person2.personalNumber;
            
            return (
              <div className="bg-morado/5 rounded-xl p-4 border border-morado/10">
                <h5 className="font-semibold text-gray-700 mb-3">Cómo se expresan en pareja:</h5>
                <div className="space-y-4">
                  {/* Persona 1 */}
                  <div className="bg-white/60 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-morado/10 flex items-center justify-center text-morado font-bold text-sm">
                        {person1.personalNumber}
                      </div>
                      <span className="font-semibold text-morado">{person1.name}</span>
                      {desc1 && <span className="text-xs text-morado">• {desc1.shortDesc}</span>}
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {sameNumber 
                        ? getSameNumberVariation(person1.personalNumber, 0)
                        : (desc1?.inRelationship || person1.group.inRelationship)
                      }
                    </p>
                    {desc1 && !sameNumber && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="text-xs bg-morado/10 text-morado px-2 py-0.5 rounded-full">
                          ✨ {desc1.gift?.split('.')[0]}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Persona 2 */}
                  <div className="bg-white/60 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center text-pink-700 font-bold text-sm">
                        {person2.personalNumber}
                      </div>
                      <span className="font-semibold text-pink-700">{person2.name}</span>
                      {desc2 && <span className="text-xs text-pink-500">• {desc2.shortDesc}</span>}
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {sameNumber 
                        ? getSameNumberVariation(person2.personalNumber, 1)
                        : (desc2?.inRelationship || person2.group.inRelationship)
                      }
                    </p>
                    {desc2 && !sameNumber && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full">
                          ✨ {desc2.gift?.split('.')[0]}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Si tienen el mismo número, mostrar info adicional */}
                  {sameNumber && desc1 && (
                    <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                      <p className="text-xs text-indigo-700">
                        <span className="font-semibold">💫 Ambos son {desc1.shortDesc}:</span> {desc1.needs}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Qué significa que sean del mismo grupo o diferentes */}
          {sameGroup ? (
            <div className="bg-green-50 rounded-xl p-5 border border-green-200">
              <h5 className="font-semibold text-green-800 mb-2">
                {SAME_GROUP_MEANINGS[person1.group.key]?.title || '✨ Mismo Grupo'}
              </h5>
              <p className="text-sm text-green-700 leading-relaxed mb-3">
                {SAME_GROUP_MEANINGS[person1.group.key]?.description}
              </p>
              <div className="bg-green-100/50 rounded-lg p-3">
                <p className="text-xs text-green-700">
                  <span className="font-semibold">Dinámica:</span> {SAME_GROUP_MEANINGS[person1.group.key]?.dynamic}
                </p>
              </div>
              <div className="mt-3 bg-white/60 rounded-lg p-3 border border-green-100">
                <p className="text-xs text-green-800">
                  <span className="font-semibold">💡 Consejo:</span> {SAME_GROUP_MEANINGS[person1.group.key]?.advice}
                </p>
              </div>
            </div>
          ) : (
            (() => {
              const diffMeaning = getDifferentGroupMeaning(person1.group.key, person2.group.key);
              return diffMeaning ? (
                <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                  <h5 className="font-semibold text-amber-800 mb-2">
                    {diffMeaning.title}
                  </h5>
                  <p className="text-sm text-amber-700 leading-relaxed mb-3">
                    {diffMeaning.description}
                  </p>
                  <div className="bg-amber-100/50 rounded-lg p-3">
                    <p className="text-xs text-amber-700">
                      <span className="font-semibold">Dinámica:</span> {diffMeaning.dynamic}
                    </p>
                  </div>
                  <div className="mt-3 bg-white/60 rounded-lg p-3 border border-amber-100">
                    <p className="text-xs text-amber-800">
                      <span className="font-semibold">💡 Consejo:</span> {diffMeaning.advice}
                    </p>
                  </div>
                </div>
              ) : null;
            })()
          )}
          
          {/* Vibración dominante */}
          {results.dominantGroup && (
            <div className="bg-morado/10 rounded-xl p-4 text-center">
              <p className="text-sm text-morado">
                <span className="font-semibold">Vibración dominante de la relación:</span>
              </p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className="text-2xl">{results.dominantGroup.emoji}</span>
                <span className="text-morado font-semibold">{results.dominantGroup.name}</span>
              </div>
              <p className="text-xs text-morado mt-2">
                Esta energía define el "tono" general de cómo funcionan como pareja.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Significados de cada número personal */}
      {(() => {
        const sameNumber = person1.personalNumber === person2.personalNumber;
        const meaning1 = getNumberMeaning(person1.personalNumber);
        const meaning2 = getNumberMeaning(person2.personalNumber);
        
        return (
          <div className="bg-white/70 rounded-2xl p-5 border border-morado/20">
            <h4 className="font-semibold text-gray-800 mb-4">
              💑 En la Relación...
            </h4>
            
            <div className="space-y-4">
              {/* Persona 1 */}
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-morado/10 flex items-center justify-center text-morado font-bold flex-shrink-0">
                  {person1.personalNumber}
                </div>
                <div>
                  <span className="font-semibold text-morado">{person1.name}</span>
                  <span className="text-xs text-gray-400 ml-2">({meaning1.keyword})</span>
                  <p className="text-sm text-gray-600">
                    {sameNumber 
                      ? getSameNumberVariation(person1.personalNumber, 0)
                      : meaning1.inRelationship
                    }
                  </p>
                </div>
              </div>
              
              {/* Persona 2 */}
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center text-pink-700 font-bold flex-shrink-0">
                  {person2.personalNumber}
                </div>
                <div>
                  <span className="font-semibold text-pink-700">{person2.name}</span>
                  <span className="text-xs text-gray-400 ml-2">({meaning2.keyword})</span>
                  <p className="text-sm text-gray-600">
                    {sameNumber 
                      ? getSameNumberVariation(person2.personalNumber, 1)
                      : meaning2.inRelationship
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tip según tipo de pareja */}
      <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
        <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
          💡 Tip para su Relación
        </h4>
        <p className="text-sm text-amber-700 leading-relaxed">
          {results.coupleTypeKey === 'natural' && 
            'Aprovechen su conexión natural para crecer juntos. Cuidado con dar por sentado al otro. Mantengan viva la chispa explorando nuevas experiencias.'}
          {results.coupleTypeKey === 'compatible' && 
            'Su fuerza está en la complementariedad. Aprendan a valorar las diferencias como oportunidades de crecimiento. La comunicación abierta es su mejor aliada.'}
          {results.coupleTypeKey === 'challenge' && 
            'Transformen los desafíos en oportunidades. La paciencia y el humor son esenciales. Celebren las pequeñas victorias y no tomen todo personal.'}
          {results.coupleTypeKey === 'neutral' && 
            'Cultiven la conexión activamente. Busquen actividades que los apasionen a ambos. No dejen que la rutina enfríe la relación.'}
        </p>
      </div>
    </motion.div>
  );
}

/**
 * Componente principal
 */
export default function SynastrySection({ currentProfile }) {
  // Estado del formulario de pareja
  const [partnerData, setPartnerData] = useState({
    id: null,
    name: '',
    lastName: '',
    scdLastName: '',
    birthDate: ''
  });
  
  // Estado de parejas guardadas
  const [savedPartners, setSavedPartners] = useState([]);
  const [isLoadingPartners, setIsLoadingPartners] = useState(true);
  const [showPartnerSelector, setShowPartnerSelector] = useState(false);
  
  // Ref para cerrar dropdown al hacer clic fuera
  const dropdownRef = useRef(null);
  
  // Estado de resultados
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dataSaved, setDataSaved] = useState(false);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPartnerSelector(false);
      }
    };

    if (showPartnerSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPartnerSelector]);

  // Cargar parejas guardadas al montar
  useEffect(() => {
    const loadPartners = async () => {
      setIsLoadingPartners(true);
      
      // Intentar cargar desde servidor
      const serverPartners = await fetchPartners();
      
      if (serverPartners.length > 0) {
        setSavedPartners(serverPartners);
        
        // Cargar la pareja activa en el formulario
        const activePartner = serverPartners.find(p => p.isActive) || serverPartners[0];
        if (activePartner) {
          setPartnerData({
            id: activePartner.id,
            name: activePartner.name,
            lastName: activePartner.apellidoPaterno || '',
            scdLastName: activePartner.apellidoMaterno || '',
            birthDate: activePartner.birthDate?.split('T')[0] || ''
          });
        }
      } else {
        // Fallback a localStorage
        const localData = loadPartnerDataLocal();
        if (localData) {
          setPartnerData({
            id: null,
            name: localData.name || '',
            lastName: localData.lastName || '',
            scdLastName: localData.scdLastName || '',
            birthDate: localData.birthDate || ''
          });
        }
      }
      
      setIsLoadingPartners(false);
    };
    
    loadPartners();
  }, []);

  // Datos del usuario actual
  const userData = useMemo(() => ({
    name: currentProfile?.name || '',
    lastName: currentProfile?.apellidoPaterno || '',
    scdLastName: currentProfile?.apellidoMaterno || '',
    birthDate: currentProfile?.birth || ''
  }), [currentProfile]);

  // Validar si podemos calcular
  const canCalculate = useMemo(() => {
    return (
      userData.birthDate &&
      partnerData.name.trim() &&
      partnerData.birthDate
    );
  }, [userData, partnerData]);

  // Manejar cambios en el formulario
  // Si cambian nombre o fecha de nacimiento, resetear el ID para crear nueva pareja
  const handleChange = (field, value) => {
    setPartnerData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Si se cambia nombre o fecha y hay un ID existente, 
      // resetear ID para que se cree una nueva pareja
      if ((field === 'name' || field === 'birthDate') && prev.id) {
        // Solo resetear si el valor realmente cambió
        if (prev[field] !== value) {
          console.log('🔄 Datos fundamentales cambiados, se creará nueva pareja');
          newData.id = null;
        }
      }
      
      return newData;
    });
    setResults(null);
    setDataSaved(false);
  };

  // Seleccionar una pareja guardada
  const handleSelectPartner = async (partner) => {
    const newPartnerData = {
      id: partner.id,
      name: partner.name,
      lastName: partner.apellidoPaterno || '',
      scdLastName: partner.apellidoMaterno || '',
      birthDate: partner.birthDate?.split('T')[0] || ''
    };
    
    setPartnerData(newPartnerData);
    setShowPartnerSelector(false);
    setResults(null);
    
    // Activar en servidor
    await activatePartnerOnServer(partner.id);
    
    // Auto-calcular compatibilidad si hay datos válidos
    if (userData.birthDate && newPartnerData.name && newPartnerData.birthDate) {
      setIsCalculating(true);
      setTimeout(() => {
        try {
          const synastryResults = calculateSynastry(userData, newPartnerData);
          setResults(synastryResults);
        } catch (err) {
          console.error('Error calculando sinastría:', err);
          setError(err.message || 'Error al calcular la compatibilidad');
        } finally {
          setIsCalculating(false);
        }
      }, 400);
    }
  };

  // Crear nueva pareja (limpiar formulario)
  const handleNewPartner = () => {
    setPartnerData({
      id: null,
      name: '',
      lastName: '',
      scdLastName: '',
      birthDate: ''
    });
    setShowPartnerSelector(false);
    setResults(null);
  };

  // Eliminar pareja
  const handleDeletePartner = async (partnerId, e) => {
    e.stopPropagation();
    
    if (!confirm('¿Eliminar esta pareja guardada?')) return;
    
    const success = await deletePartnerFromServer(partnerId);
    if (success) {
      setSavedPartners(prev => prev.filter(p => p.id !== partnerId));
      
      // Si era la pareja activa, limpiar formulario
      if (partnerData.id === partnerId) {
        handleNewPartner();
      }
    }
  };

  // Guardar datos de pareja
  const handleSavePartner = async () => {
    if (!partnerData.name || !partnerData.birthDate) return;
    
    setIsSaving(true);
    
    // Guardar en servidor
    const savedPartner = await savePartnerToServer(partnerData, partnerData.id);
    
    if (savedPartner) {
      // Actualizar lista de parejas
      setSavedPartners(prev => {
        const exists = prev.find(p => p.id === savedPartner.id);
        if (exists) {
          return prev.map(p => p.id === savedPartner.id ? {
            ...savedPartner,
            fullName: [savedPartner.name, savedPartner.apellidoPaterno, savedPartner.apellidoMaterno].filter(Boolean).join(' ')
          } : { ...p, isActive: false });
        } else {
          return [{
            ...savedPartner,
            fullName: [savedPartner.name, savedPartner.apellidoPaterno, savedPartner.apellidoMaterno].filter(Boolean).join(' ')
          }, ...prev.map(p => ({ ...p, isActive: false }))];
        }
      });
      
      // Actualizar ID si era nueva
      setPartnerData(prev => ({ ...prev, id: savedPartner.id }));
      setDataSaved(true);
    } else {
      // Fallback a localStorage
      savePartnerDataLocal(partnerData);
      setDataSaved(true);
    }
    
    setIsSaving(false);
    setTimeout(() => setDataSaved(false), 2000);
  };

  // Calcular sinastría
  const handleCalculate = async () => {
    if (!canCalculate) return;
    
    setIsCalculating(true);
    setError(null);
    
    // Guardar automáticamente al calcular
    if (partnerData.name && partnerData.birthDate) {
      const savedPartner = await savePartnerToServer(partnerData, partnerData.id);
      if (savedPartner) {
        setPartnerData(prev => ({ ...prev, id: savedPartner.id }));
        
        // Actualizar lista de parejas
        setSavedPartners(prev => {
          const exists = prev.find(p => p.id === savedPartner.id);
          if (exists) {
            return prev.map(p => p.id === savedPartner.id ? {
              ...savedPartner,
              fullName: [savedPartner.name, savedPartner.apellidoPaterno, savedPartner.apellidoMaterno].filter(Boolean).join(' ')
            } : { ...p, isActive: false });
          } else {
            return [{
              ...savedPartner,
              fullName: [savedPartner.name, savedPartner.apellidoPaterno, savedPartner.apellidoMaterno].filter(Boolean).join(' ')
            }, ...prev.map(p => ({ ...p, isActive: false }))];
          }
        });
      }
    }
    
    setTimeout(() => {
      try {
        const synastryResults = calculateSynastry(userData, partnerData);
        setResults(synastryResults);
      } catch (err) {
        console.error('Error calculando sinastría:', err);
        setError(err.message || 'Error al calcular la compatibilidad');
      } finally {
        setIsCalculating(false);
      }
    }, 600);
  };

  // URL externa para análisis completo (tienda de reportes)
  const analysisUrl = 'https://tienda.numerologia-cotidiana.com/#reportes';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-morado rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <Heart className="w-8 h-8" fill="white" />
          <h2 className="text-2xl font-bold">Compatibilidad de Pareja</h2>
        </div>
        <p className="text-white/90 text-sm">
          Descubre qué tipo de pareja son según la numerología. 
          Solo necesitas tu fecha de nacimiento y la de tu pareja.
        </p>
      </div>

      {/* Formulario */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Datos del usuario (solo lectura) */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-morado/20 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-morado" />
            <h3 className="font-semibold text-morado">Tus datos</h3>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Nombre completo</label>
              <div className="px-3 py-2 bg-morado/5 rounded-lg text-morado text-sm font-medium">
                {[userData.name, userData.lastName, userData.scdLastName].filter(Boolean).join(' ') || 'No disponible'}
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Fecha de nacimiento</label>
              <div className="px-3 py-2 bg-morado/5 rounded-lg text-morado text-sm">
                {userData.birthDate ? new Date(userData.birthDate + 'T00:00:00').toLocaleDateString('es-MX', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                }) : 'No disponible'}
              </div>
            </div>
          </div>
        </div>

        {/* Datos de la pareja (editable) */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-pink-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-600" />
              <h3 className="font-semibold text-pink-800">Datos de tu pareja</h3>
            </div>
            <div className="flex items-center gap-2">
              {/* Selector de parejas guardadas - SIEMPRE visible */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowPartnerSelector(!showPartnerSelector)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition ${
                    savedPartners.length > 0 
                      ? 'bg-morado/10 text-morado hover:bg-morado/20' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Users className="w-3 h-3" />
                  <span>
                    {savedPartners.length > 0 
                      ? `${savedPartners.length} guardada${savedPartners.length !== 1 ? 's' : ''}` 
                      : 'Mis parejas'}
                  </span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${showPartnerSelector ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown de parejas */}
                <AnimatePresence>
                  {showPartnerSelector && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 top-full mt-1 w-72 bg-white rounded-xl shadow-lg border border-morado/20 z-50 overflow-hidden"
                    >
                      {/* Header del dropdown */}
                      <div className="p-3 bg-morado/5 border-b border-morado/10">
                        <h4 className="text-sm font-semibold text-morado flex items-center gap-2">
                          <Heart className="w-4 h-4 text-pink-500" />
                          Parejas Guardadas
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          {savedPartners.length > 0 
                            ? 'Selecciona una pareja para ver su análisis'
                            : 'Aún no tienes parejas guardadas'}
                        </p>
                      </div>
                      
                      {/* Botón nueva pareja */}
                      <div className="p-2 border-b border-gray-100">
                        <button
                          onClick={handleNewPartner}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-morado hover:bg-morado/5 transition"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Agregar nueva pareja</span>
                        </button>
                      </div>
                      
                      {/* Lista de parejas */}
                      {savedPartners.length > 0 ? (
                        <div className="max-h-48 overflow-y-auto">
                          {savedPartners.map((partner) => (
                            <div
                              key={partner.id}
                              onClick={() => handleSelectPartner(partner)}
                              className={`
                                flex items-center justify-between px-3 py-3 cursor-pointer transition border-b border-gray-50 last:border-0
                                ${partner.id === partnerData.id ? 'bg-pink-50' : 'hover:bg-gray-50'}
                              `}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                  partner.id === partnerData.id ? 'bg-pink-500' : 'bg-morado/50'
                                }`}>
                                  {(partner.name || '?')[0].toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="text-sm font-medium text-gray-800 block truncate">
                                    {partner.fullName || partner.name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {partner.birthDate ? new Date(partner.birthDate).toLocaleDateString('es-MX', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                      timeZone: 'UTC'
                                    }) : ''}
                                  </span>
                                </div>
                                {partner.isActive && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                    Activa
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={(e) => handleDeletePartner(partner.id, e)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition flex-shrink-0 ml-2"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 text-center">
                          <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
                            <Heart className="w-6 h-6 text-gray-400" />
                          </div>
                          <p className="text-sm text-gray-500">
                            Ingresa los datos de tu pareja y presiona <strong>"Guardar"</strong> para guardarla aquí.
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Botón guardar */}
              <motion.button
                onClick={handleSavePartner}
                disabled={!partnerData.name || !partnerData.birthDate || isSaving}
                animate={dataSaved ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 0.3 }}
                className={`
                  flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors
                  ${dataSaved 
                    ? 'bg-green-100 text-green-700 ring-2 ring-green-300' 
                    : 'bg-pink-100 text-pink-700 hover:bg-pink-200'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                {isSaving ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : dataSaved ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <Save className="w-3 h-3" />
                )}
                {dataSaved ? '¡Guardado!' : 'Guardar'}
              </motion.button>
            </div>
          </div>
          
          {/* Loading indicator */}
          {isLoadingPartners ? (
            <div className="flex items-center justify-center py-8 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span className="text-sm">Cargando...</span>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1 flex items-center gap-1">
                  Nombre(s) *
                  <Info className="w-3 h-3 text-gray-400 cursor-help" title="Ingresa el nombre completo como aparece en su acta de nacimiento" />
                </label>
                <input
                  type="text"
                  value={partnerData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Ej: María José"
                  className="w-full px-3 py-2 rounded-lg border border-pink-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Apellido paterno</label>
                  <input
                    type="text"
                    value={partnerData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    placeholder="Apellido paterno"
                    className="w-full px-3 py-2 rounded-lg border border-pink-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Apellido materno</label>
                  <input
                    type="text"
                    value={partnerData.scdLastName}
                    onChange={(e) => handleChange('scdLastName', e.target.value)}
                    placeholder="Apellido materno"
                    className="w-full px-3 py-2 rounded-lg border border-pink-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1 flex items-center gap-1">
                  Fecha de nacimiento *
                  <Info className="w-3 h-3 text-gray-400 cursor-help" title="La fecha exacta de nacimiento es esencial para un cálculo preciso" />
                </label>
                <input
                  type="date"
                  value={partnerData.birthDate}
                  onChange={(e) => handleChange('birthDate', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 rounded-lg border border-pink-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none text-sm"
                />
              </div>
              
              {/* Indicador de pareja guardada */}
              <AnimatePresence>
                {partnerData.id && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-lg"
                  >
                    <Check className="w-3 h-3" />
                    <span>Pareja guardada en tu cuenta</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Botón de calcular */}
      <div className="flex justify-center">
        <motion.button
          onClick={handleCalculate}
          disabled={!canCalculate || isCalculating}
          whileHover={{ scale: canCalculate ? 1.02 : 1 }}
          whileTap={{ scale: canCalculate ? 0.98 : 1 }}
          className={`
            flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg
            transition-all duration-300 shadow-lg
            ${canCalculate && !isCalculating
              ? 'bg-morado text-white hover:shadow-xl'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }
          `}
        >
          {isCalculating ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              <span>Calculando...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-6 h-6" />
              <span>Descubrir Compatibilidad</span>
            </>
          )}
        </motion.button>
      </div>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Resultados */}
      <AnimatePresence>
        {results && <CompatibilityResult results={results} />}
      </AnimatePresence>

      {/* CTA para análisis completo */}
      {results && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-morado rounded-2xl p-6 text-white shadow-lg"
        >
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 text-center md:text-left">
              <h4 className="text-xl font-bold mb-2">
                💎 ¿Quieres un Análisis Más Profundo?
              </h4>
              <p className="text-white/90 text-sm">
                Obtén un estudio completo de tu sinastría con etapas de vida juntos, 
                ciclos anuales, compatibilidad por nombres y mucho más.
              </p>
            </div>
            <a
              href={analysisUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-white text-morado rounded-xl font-semibold hover:bg-morado/5 transition shadow-md whitespace-nowrap"
            >
              <span>Ver Opciones</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      )}

      {/* Info si no hay resultados */}
      {!results && !error && (
        <div className="text-center text-gray-500 text-sm py-8">
          <Heart className="w-12 h-12 mx-auto mb-3 text-pink-300" />
          <p>Ingresa el nombre y fecha de nacimiento de tu pareja para descubrir su compatibilidad.</p>
        </div>
      )}
    </motion.div>
  );
}
